/* A character as a short code to share, and back: what differs from a blank character, as JSON with field
   names and fixed words as list places and content names as 4-character tokens, deflated and written in
   base64url. The format is described in README.md, beside this file. */

import { BRAWLERS_STRIKE, classDomains, ELEMENTS } from '../catalog';
import { classes } from '../content/classes';
import { ancestries } from '../content/ancestries';
import { communities } from '../content/communities';
import { transformations } from '../content/transformations';
import allDomainCards from '../content/domains/cards';
import { AUGMENTS, IKONIS_DAMAGE_TYPES, IKONIS_RANGES, SCRAP } from '../content/motherboard';
import { COMPANION_SHEET, EXPERIENCE_EXAMPLES } from '../content/sheetText';
import equipment from '../data/equipment.json';
import beastforms from '../data/beastforms.json';
import items from '../data/items.json';
import consumables from '../data/consumables.json';
import { Character, emptyCharacter, kitItems } from '../model';
import { compress, decompress } from '../deflate';

/** The code format; a code starts with it, so a later format can still read older codes. */
const VERSION = '1';

/** A code that cannot be read, with a message for the player. */
export class CodeError extends Error {}

/** Field names and fixed values, coded by their place here. Codes depend on the order: only ever add to the end. */
export const WORDS = [
  'className', 'subclass', 'ancestry', 'mixedAncestry', 'community', 'transformation', 'traits', 'primary', 'secondary', 'armor',
  'strikeTrait', 'potion', 'classItem', 'craft', 'droppedKit', 'loot', 'inventoryWeapons', 'useIkonis', 'ikonis', 'description',
  'background', 'backgroundQuestions', 'name', 'pronouns', 'experiences', 'purposeful', 'domainCards', 'connections', 'connectionQuestions', 'patronName',
  'element', 'sphere', 'strangePattern', 'stances', 'activeStance', 'stanceSheet', 'companionName', 'companionAnimal', 'companionExperiences', 'marks',
  'levelUps', 'multiclass', 'beastform', 'finished', 'progress', 'trait', 'range', 'damageType', 'built', 'installed',
  'scrap', 'relics', 'quantum', 'level', 'experience', 'advancements', 'card', 'swap', 'companion', 'companionExperience',
  'stance', 'kind', 'tier', 'from', 'to', 'option', 'step', 'domain', 'base', 'evolution',
  'mode', 'agility', 'strength', 'finesse', 'instinct', 'presence', 'knowledge', 'hp', 'stress', 'hope',
  'handfuls', 'bags', 'chest', 'favor', 'companionStress', 'focus', 'clothes', 'eyes', 'body', 'skin',
  'attitude', 'domainCard', 'evasion', 'proficiency', 'half', 'full', 'quick', 'build', 'damage', 'armorOff',
];

/** Every name a character can take from the content, in any version: these are coded as tokens. */
export function contentNames(): string[] {
  const named: Array<Array<{ name: string }>> = [ancestries, communities, transformations, equipment.weapons, equipment.wheelchairs, equipment.armor, items, consumables, allDomainCards, beastforms, AUGMENTS, COMPANION_SHEET.options];
  return [
    ...classes.flatMap((c) => [
      c.name,
      ...classDomains(c),
      ...Object.values(c.subclasses ?? {}).flatMap((s) => [s.name, ...Object.values(s.martialForm?.tiers ?? {}).flat().map((st) => st.name)]),
      ...(c.inventory ? [...c.inventory.chooseBetween, ...c.inventory.andEither, ...(c.inventory.thenDecideWhatYouUseForYourCraft ?? []), ...kitItems(c.inventory.take)] : []),
      ...Object.values(c.characterDescription ?? {}).flat(),
    ]),
    ...named.flatMap((list) => list.map((x) => x.name)),
    BRAWLERS_STRIKE.name,
    ...SCRAP.flatMap((g) => g.items.map((i) => i.name)),
    ...EXPERIENCE_EXAMPLES.flatMap((g) => g.items),
    ...COMPANION_SHEET.examples,
    ...ELEMENTS,
    ...IKONIS_RANGES.map((r) => r.range),
    ...IKONIS_DAMAGE_TYPES,
  ];
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** A name's token: 24 bits of its FNV-1a hash in base64url, so it never changes when other content is added. */
export function nameToken(name: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) h = Math.imul(h ^ name.charCodeAt(i), 0x01000193);
  return [18, 12, 6, 0].map((shift) => B64[(h >>> (8 + shift)) & 63]).join('');
}

let names: { token: Map<string, string>; name: Map<string, string> } | undefined;
function nameTables() {
  if (!names) {
    const unique = Array.from(new Set(contentNames()));
    names = { token: new Map(unique.map((n) => [n, nameToken(n)])), name: new Map(unique.map((n) => [nameToken(n), n])) };
  }
  return names;
}

// Strings: '!' and a WORDS place, '~' and a name token, '\' before typed text that starts with one of those marks.
function packString(s: string): string {
  const word = WORDS.indexOf(s);
  if (word >= 0) return `!${word.toString(36)}`;
  const token = nameTables().token.get(s);
  if (token) return `~${token}`;
  return /^[!~\\]/.test(s) ? `\\${s}` : s;
}

/** A WORDS place or name token this version does not have: a code from a newer site, or renamed content. */
const unknown = () => new CodeError('This code names something this site does not have.');

function word(place: string): string {
  const found = WORDS[parseInt(place, 36)];
  if (found === undefined) throw unknown();
  return found;
}

function unpackString(s: string): string {
  if (s[0] === '\\') return s.slice(1);
  if (s[0] === '!') return word(s.slice(1));
  if (s[0] !== '~') return s;
  const found = nameTables().name.get(s.slice(1));
  if (found === undefined) throw unknown();
  return found;
}

// Object keys: a WORDS place, or '_' and the packed key (an augment or scrap name).
const packKey = (k: string) => (WORDS.includes(k) ? WORDS.indexOf(k).toString(36) : `_${packString(k)}`);
const unpackKey = (k: string) => (k[0] === '_' ? unpackString(k.slice(1)) : word(k));

type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

function pack(value: Json): Json {
  if (typeof value === 'string') return packString(value);
  if (Array.isArray(value)) return value.map(pack);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [packKey(k), pack(v)]));
  return value;
}

function unpack(value: Json): Json {
  if (typeof value === 'string') return unpackString(value);
  if (Array.isArray(value)) return value.map(unpack);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [unpackKey(k), unpack(v)]));
  return value;
}

// The most a code may inflate to; a character is a few kilobytes.
const MAX_BYTES = 200_000;
const tooLarge = () => new CodeError('This code is too large to be a character.');

const toBase64url = (bytes: Uint8Array) =>
  btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join('')).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const fromBase64url = (text: string) => Uint8Array.from(atob(text.replace(/-/g, '+').replace(/_/g, '/')), (ch) => ch.charCodeAt(0));

/** The character's share code. */
export async function characterCode(c: Character): Promise<string> {
  const blank = emptyCharacter() as unknown as Record<string, Json>;
  const changed = Object.entries(c).filter(([k, v]) => v !== undefined && JSON.stringify(v) !== JSON.stringify(blank[k]));
  const json = JSON.stringify(pack(Object.fromEntries(changed) as Json));
  return VERSION + toBase64url(await compress(new TextEncoder().encode(json), 'deflate-raw'));
}

/** Keeps the fields whose kind matches a blank character's, so a hand-edited code cannot break the sheet. */
function withDefaults(data: Record<string, Json>): Character {
  const blank = emptyCharacter() as unknown as Record<string, Json>;
  const kind = (v: unknown) => (Array.isArray(v) ? 'array' : v === null ? 'null' : typeof v);
  const fits = (k: string, v: Json) => !(k in blank) || kind(v) === kind(blank[k]) || (k === 'multiclass' && kind(v) === 'object');
  return { ...emptyCharacter(), ...Object.fromEntries(Object.entries(data).filter(([k, v]) => fits(k, v))) } as Character;
}

/** The character a share code holds; throws a CodeError with a message for the player when it is not one. */
export async function readCharacterCode(code: string): Promise<Character> {
  const text = code.replace(/\s+/g, '');
  if (text[0] !== VERSION || !/^[A-Za-z0-9_-]+$/.test(text.slice(1))) throw new CodeError('This is not a dhtools character code.');
  let data: Json;
  try {
    data = unpack(JSON.parse(new TextDecoder().decode(await decompress(fromBase64url(text.slice(1)), 'deflate-raw', MAX_BYTES, tooLarge))));
  } catch (error) {
    throw error instanceof CodeError ? error : new CodeError('This code is damaged: check it was copied whole.');
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new CodeError('This code does not hold a character.');
  return withDefaults(data);
}
